import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import CustomLoader from "./CustomLoader";
import formatCurrency from "../helper/formatCurrency";

interface Transaction {
  id: number;
  admin_id: number;
  user_id: number;
  amount: number;
  type: string;
  remarks: string;
  recipient_type: string;
  performer_name: string | null;
  performer_role: string | null;
  recipient_name: string | null;
  recipient_role: string | null;
  created_at: string;
  performer_before_balance?: number | null;
  performer_after_balance?: number | null;
  receiver_before_balance?: number | null;
  receiver_after_balance?: number | null;
}

const PointTransactionReport: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  const fetchTransactions = async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/userCreditTransactions`, {
        params: {
          page: pageNum,
          pageSize: limit,
          search: searchTerm.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });

      const { data, pagination } = response.data;
      setTransactions(data || []);
      setTotalPages(pagination?.totalPages || 1);
      setPage(pagination?.currentPage || 1);
    } catch (err: any) {
      console.error("Failed to fetch transaction history:", err);
      setError("Failed to fetch transaction history.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (page === 1) {
      fetchTransactions(1);
    } else {
      setPage(1);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    if (page === 1) {
      setLoading(true);
      setError(null);
      api.get(`/userCreditTransactions`, {
        params: {
          page: 1,
          pageSize: limit,
        },
      })
        .then((response) => {
          const { data, pagination } = response.data;
          setTransactions(data || []);
          setTotalPages(pagination?.totalPages || 1);
          setPage(pagination?.currentPage || 1);
        })
        .catch((err: any) => {
          console.error("Failed to fetch transaction history:", err);
          setError("Failed to fetch transaction history.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setPage(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <div className="min-h-screen space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Point Transaction Report</h1>
        <p className="text-sm text-gray-500">
          Track all point creations, deposits, and withdrawals across admin, backoffice, agent, and player accounts.
        </p>
      </div>

      {/* Filter Section */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl border shadow-sm">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</label>
          <input
            className="rounded border border-gray-300 px-4 py-1.5 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From Date</label>
          <input
            type="date"
            className="rounded border border-gray-300 px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Date</label>
          <input
            type="date"
            className="rounded border border-gray-300 px-4 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="flex items-end gap-2 h-14 pt-5">
          <button
            onClick={handleSearch}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition shadow-sm"
          >
            Search
          </button>
          <button
            onClick={handleClear}
            className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold text-sm transition shadow-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <CustomLoader />
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-200">
          {error}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
            <table className="w-full text-sm truncate">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="py-3 px-6 border-b text-left">S.No.</th>
                  <th className="py-3 px-6 border-b text-left">Sender / Performer</th>
                  <th className="py-3 px-6 border-b text-left">Receiver / Recipient</th>
                  <th className="py-3 px-6 border-b text-left">Amount</th>
                  <th className="py-3 px-6 border-b text-left">Type</th>
                  <th className="py-3 px-6 border-b text-left">Remarks</th>
                  <th className="py-3 px-6 border-b text-left">Date & Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-800">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-500 font-medium">
                      No point transactions recorded.
                    </td>
                  </tr>
                ) : (
                  transactions.map((item, index) => {
                    let performerName = item.performer_name || `ID: ${item.admin_id}`;
                    let performerRole = item.performer_role || "system";
                    let recipientName = item.recipient_name || `ID: ${item.user_id}`;
                    let recipientRole = item.recipient_role || item.recipient_type || "player";
                    let performerBefore: number | null = null;
                    let performerAfter: number | null = null;
                    let receiverBefore: number | null = null;
                    let receiverAfter: number | null = null;
                    let displayRemarks = item.remarks;

                    if (item.remarks && item.remarks.includes(" | ")) {
                      const parts = item.remarks.split(" | ");
                      if (parts.length >= 3) {
                        const senderPart = parts[0];
                        const receiverPart = parts[1];

                        let remarksStartIndex = 2;
                        if (parts[2] && parts[2].startsWith("Balances:")) {
                          const balMatch = parts[2].match(/^Balances:\s*(.+?),(.+?),(.+?),(.+?)$/);
                          if (balMatch) {
                            performerBefore = parseFloat(balMatch[1]);
                            performerAfter = parseFloat(balMatch[2]);
                            receiverBefore = parseFloat(balMatch[3]);
                            receiverAfter = parseFloat(balMatch[4]);
                            remarksStartIndex = 3;
                          }
                        }

                        const senderMatch = senderPart.match(/^Sender:\s*(.+?)\s*\((.+?)\)$/i);
                        const receiverMatch = receiverPart.match(/^Receiver:\s*(.+?)\s*\((.+?)\)$/i);

                        if (senderMatch) {
                          performerName = senderMatch[1];
                          performerRole = senderMatch[2];
                        }
                        if (receiverMatch) {
                          recipientName = receiverMatch[1];
                          recipientRole = receiverMatch[2];
                        }
                        displayRemarks = parts.slice(remarksStartIndex).join(" | ");
                      }
                    }

                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition">
                        <td className="py-4 px-6 border-b font-medium">
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className="py-4 px-6 border-b">
                          <span className="font-semibold text-gray-800 block">{performerName}</span>
                          <span className="text-xs text-gray-400 capitalize">{performerRole}</span>
                          {performerRole !== "admin" && (performerBefore !== null || (item.performer_before_balance !== undefined && item.performer_before_balance !== null)) && (
                            <span className="text-xs text-gray-500 block mt-1 font-medium bg-gray-100 px-2 py-0.5 rounded w-max">
                              Bal: {formatCurrency(Number(performerBefore !== null ? performerBefore : item.performer_before_balance))} → {formatCurrency(Number(performerAfter !== null ? performerAfter : item.performer_after_balance))}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 border-b">
                          <span className="font-semibold text-gray-800 block">{recipientName}</span>
                          <span className="text-xs text-gray-400 capitalize">{recipientRole}</span>
                          {(receiverBefore !== null || (item.receiver_before_balance !== undefined && item.receiver_before_balance !== null)) && (
                            <span className="text-xs text-gray-500 block mt-1 font-medium bg-gray-100 px-2 py-0.5 rounded w-max">
                              Bal: {formatCurrency(Number(receiverBefore !== null ? receiverBefore : item.receiver_before_balance))} → {formatCurrency(Number(receiverAfter !== null ? receiverAfter : item.receiver_after_balance))}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 border-b font-semibold text-gray-800">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="py-4 px-6 border-b">
                          <span
                            className={`px-2 py-1 rounded text-xs font-bold ${item.type === "credited"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                              }`}
                          >
                            {item.type === "credited" ? "Deposit" : "Withdrawal"}
                          </span>
                        </td>
                        <td className="py-4 px-6 border-b text-xs text-gray-500 whitespace-normal break-words max-w-[250px]">
                          {displayRemarks}
                        </td>
                        <td className="py-4 px-6 border-b text-gray-500">
                          {new Date(item.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-end gap-2 mt-4">
            <button
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className={`px-4 py-2 border rounded-lg text-sm font-semibold transition ${page === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              Prev
            </button>
            <div className="flex items-center px-4 text-sm font-medium text-gray-700">
              Page {page} of {totalPages}
            </div>
            <button
              disabled={page === totalPages}
              onClick={() => handlePageChange(page + 1)}
              className={`px-4 py-2 border rounded-lg text-sm font-semibold transition ${page === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PointTransactionReport;
